import React, { useState, useEffect, Fragment } from 'react';
import { Loader } from 'components/Loader/Loader';
import { format as dateFormat } from 'utils/date';
import {
  AddButtonWrapper,
  AdditionalContentWrapper,
  FilterWrapper,
  TableWrapper,
  ThInner,
  SortIcon,
  DisplayInfo,
  SearchWrapper,
} from './Table.styled';

interface Props {
  data: any;
  properties: {
    alias: string;
    name: string;
    sortable?: boolean;
    format?: FieldFormat;
  }[];
  action?: { display: (item: any) => JSX.Element; callback?: Function };
  isLoading?: boolean;
  emptyState?: JSX.Element;
  addButton?: JSX.Element;
  filter?: JSX.Element;
  search?: JSX.Element;
}

export enum FieldFormat {
  Number,
  Percentage,
  Date,
  Boolean,
  DDStatus,
}

const mapDDStatus = (value: string | null) =>
  value === 'APPROVED' ? 'Approved' : value === 'DECLINED' ? 'Declined' : 'Awaiting assessment';

const display = (value: string, format: FieldFormat | undefined) => {
  if (!format) {
    return <span>{value || '-'}</span>;
  }

  if (
    [FieldFormat.Number, FieldFormat.Percentage, FieldFormat.Date, FieldFormat.Boolean, FieldFormat.DDStatus].indexOf(
      format,
    ) > -1
  ) {
    return (
      <span style={{ display: 'block', textAlign: 'left' }}>
        {format === FieldFormat.Percentage && <>&#37;</>}
        {format === FieldFormat.Date && <>{dateFormat(value).fromBackendToView()}</>}
        {format === FieldFormat.Boolean && <>{value ? 'Activated' : 'Disabled'}</>}
        {format === FieldFormat.DDStatus && <>{mapDDStatus(value)}</>}
      </span>
    );
  }
};

const Table: React.FC<Props> = ({ data, properties, isLoading, emptyState, action, addButton, filter, search }) => {
  const [dataToDisplay, setDataToDisplay] = useState([]);

  useEffect(() => {
    setDataToDisplay(data);
  }, [action, data, properties]);

  return (
    <>
      <AdditionalContentWrapper>
        <FilterWrapper>{filter}</FilterWrapper>
        <SearchWrapper>{search}</SearchWrapper>
        <AddButtonWrapper>{addButton}</AddButtonWrapper>
      </AdditionalContentWrapper>
      <div style={{ position: 'relative', width: '100%' }}>
        <TableWrapper cellSpacing={0}>
          <thead>
            <tr>
              {properties.map(({ name, alias, sortable }) => (
                <Fragment key={alias}>
                  {sortable !== false ? (
                    <th>
                      <ThInner>
                        <button onClick={() => {}}>
                          {name}
                          <SortIcon />
                        </button>
                      </ThInner>
                    </th>
                  ) : (
                    <th>
                      <ThInner>
                        <span>{name}</span>
                      </ThInner>
                    </th>
                  )}
                </Fragment>
              ))}
              {action && (
                <th>
                  <ThInner>Actions</ThInner>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <DisplayInfo>
                <td colSpan={properties.length}>
                  <Loader />
                </td>
              </DisplayInfo>
            ) : (
              <>
                {dataToDisplay.map((item, index) => (
                  <tr key={index}>
                    {properties.map(({ alias, format }) => (
                      <td key={alias}>{display(item[alias], format)}</td>
                    ))}
                    {action && (
                      <td>
                        <button
                          onClick={(e: any) => {
                            if (action.callback) {
                              action.callback(e, item);
                            }
                          }}>
                          {action.display(item)}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!dataToDisplay.length && emptyState && !isLoading && (
                  <DisplayInfo>
                    <td colSpan={properties.length}>{emptyState}</td>
                  </DisplayInfo>
                )}
              </>
            )}
          </tbody>
        </TableWrapper>
      </div>
    </>
  );
};

export default Table;
